/**
 * #933 PR 2b — Settings → Capacity: blocked days.
 *
 * "Block next Saturday for assessments" was hand SQL from a Claude session
 * (`e2e-hthd-critical-flows.test.ts` names it gap B-1). This card writes
 * the `capacity_overrides` rows the engine already honors: a closure makes
 * `createBooking` 409 and hides the day from the assessment offer and the
 * daycare preview; an expanded or special-event day carries its own limit.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: each row leads with the day and the words
 *    the row means ("Closed for all services", "25 dogs for Daycare"),
 *    derived from the same fields the engine reads.
 *  - #2 The next action is obvious: one primary Save; a native date picker
 *    (the phone's own control at 375); remove is a two-tap, never a browser
 *    confirm. A non-owner sees the list with a plain reason.
 *  - #5 Never lie about data: an empty list says which horizon it covers
 *    ("next 90 days"), so no blocked day is hidden by a filter nobody sees.
 *  - Five states: loading · error(+retry) · empty (teaching copy) · ready ·
 *    saving. Stale does not apply: the list re-reads after every write.
 *
 * Counter Test: Ted blocks next Saturday from his phone; the customer app
 * stops offering it within a minute; removing the row restores it.
 *
 * Server contract: GET /api/v2/admin/tenant/capacity-overrides?from&to,
 * POST /capacity-overrides (upsert on date + service — a second save for the
 * same day updates in place, the #723 lesson), DELETE /capacity-overrides/:id.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Input, Select, Skeleton } from '@ferro/ui';
import {
  addIsoDays,
  adminTenantCapacityApi,
  todayIsoDate,
  type TenantCapacityOverride,
  type TenantCapacityOverrideDraft,
  type TenantCapacityOverrideReason,
  type TenantCapacityOverridesView,
} from '../../lib/api.js';
import { formatBlockedDate, intInput } from './capacity-helpers.js';

interface BlockedDaysCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager). Everyone else sees read-only. */
  canEdit: boolean;
}

type Status = 'loading' | 'error' | 'ready';

/** How far ahead the list looks. The default matches the server's own (today + 90). */
export const HORIZON_OPTIONS = [
  { value: '90', label: 'Next 90 days' },
  { value: '365', label: 'Next 12 months' },
] as const;

export interface ReasonMeta {
  value: TenantCapacityOverrideReason;
  /** The option the owner picks. */
  label: string;
  /** The chip on the row. */
  chip: string;
  /** Status vocabulary only — never a brand color for meaning. */
  badge: 'closed' | 'warning' | 'success';
}

export const REASONS: ReadonlyArray<ReasonMeta> = [
  { value: 'closure', label: 'Closed — no bookings that day', chip: 'Closed', badge: 'closed' },
  { value: 'holiday', label: 'Holiday — closed', chip: 'Holiday', badge: 'closed' },
  { value: 'special_event', label: 'Special event — a different limit', chip: 'Special event', badge: 'warning' },
  { value: 'expanded', label: 'Expanded — take more dogs than usual', chip: 'Expanded', badge: 'success' },
];

export function reasonMeta(reason: string): ReasonMeta {
  return (
    REASONS.find((r) => r.value === reason) ?? {
      value: 'special_event',
      label: reason,
      chip: reason.replace(/_/g, ' '),
      badge: 'warning',
    }
  );
}

/**
 * The words a row means beside its reason chip — scope and limit, from the
 * SAME fields the engine reads (Principle 1). The chip already says
 * "Closed" / "Expanded", so this never repeats it.
 */
export function overrideSentence(o: TenantCapacityOverride, serviceName: string | null): string {
  const scope = o.serviceTypeId === null ? 'All services' : `${serviceName ?? 'One service'} only`;
  if (o.maxCapacity === null || o.maxCapacity === 0) return scope;
  return `${scope} · ${o.maxCapacity} dog${o.maxCapacity === 1 ? '' : 's'}`;
}

export interface BlockedDayForm {
  date: string;
  reason: TenantCapacityOverrideReason;
  /** '' = all services (facility-wide). */
  serviceTypeId: string;
  cap: string;
  description: string;
}

export const EMPTY_FORM: BlockedDayForm = { date: '', reason: 'closure', serviceTypeId: '', cap: '', description: '' };

export function draftFromForm(
  f: BlockedDayForm,
  today: string,
): { ok: true; draft: TenantCapacityOverrideDraft } | { ok: false; message: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.date)) return { ok: false, message: 'Pick the day.' };
  if (f.date < today) return { ok: false, message: 'That day has already passed. Pick today or a later date.' };
  let maxCapacity: number | null = null;
  if (f.reason === 'special_event' || f.reason === 'expanded') {
    const n = intInput(f.cap, 0, 5000);
    if (n === undefined) return { ok: false, message: 'The limit must be a whole number between 0 and 5000.' };
    if (f.reason === 'expanded' && n === null) {
      return { ok: false, message: 'An expanded day needs the limit it expands to.' };
    }
    maxCapacity = n;
  }
  const description = f.description.trim();
  return {
    ok: true,
    draft: {
      date: f.date,
      reason: f.reason,
      serviceTypeId: f.serviceTypeId === '' ? null : f.serviceTypeId,
      maxCapacity,
      ...(description ? { description } : {}),
    },
  };
}

export function BlockedDaysCard({ apiUrl, canEdit }: BlockedDaysCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<TenantCapacityOverridesView | null>(null);
  const [horizon, setHorizon] = useState<'90' | '365'>('90');
  const [form, setForm] = useState<BlockedDayForm>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const today = todayIsoDate();

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial'): Promise<boolean> => {
      if (mode === 'initial') {
        setStatus('loading');
        setLoadError(null);
      }
      const res = await adminTenantCapacityApi.listBlockedDays(apiUrl, {
        from: today,
        to: addIsoDays(today, Number(horizon)),
      });
      if (res.error || !res.data) {
        if (mode === 'initial') {
          setLoadError(res.error ?? 'Could not load blocked days.');
          setStatus('error');
        }
        return false;
      }
      setView(res.data);
      setStatus('ready');
      return true;
    },
    [apiUrl, horizon, today],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const serviceNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of view?.services ?? []) m.set(s.id, s.displayName);
    return m;
  }, [view]);

  const rows = useMemo(
    () =>
      [...(view?.overrides ?? [])].sort(
        (a, b) => a.date.localeCompare(b.date) || (a.serviceTypeId === null ? -1 : 1) - (b.serviceTypeId === null ? -1 : 1),
      ),
    [view],
  );

  const activeServices = useMemo(
    () => (view?.services ?? []).filter((s) => s.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [view],
  );

  const handleSave = useCallback(async () => {
    setFormError(null);
    setSaved(null);
    const parsed = draftFromForm(form, today);
    if (!parsed.ok) {
      setFormError(parsed.message);
      return;
    }
    setIsSaving(true);
    const res = await adminTenantCapacityApi.saveBlockedDay(apiUrl, parsed.draft);
    setIsSaving(false);
    if (res.error || !res.data) {
      setFormError(res.error ?? 'Could not save that day. Please try again.');
      return;
    }
    const { override, created } = res.data;
    const name = override.serviceTypeId === null ? null : (serviceNameById.get(override.serviceTypeId) ?? null);
    setSaved(
      `${created ? 'Added' : 'Updated'} ${formatBlockedDate(override.date)}: ${reasonMeta(override.reason).chip} — ${overrideSentence(override, name)}.${
        created ? ' Customers see it now.' : ' That day was already on the list, so it was changed in place.'
      }`,
    );
    setForm((f) => ({ ...EMPTY_FORM, reason: f.reason }));
    await load('refresh');
  }, [apiUrl, form, load, serviceNameById, today]);

  const handleRemove = useCallback(
    async (o: TenantCapacityOverride) => {
      setRowError(null);
      setSaved(null);
      setBusyId(o.id);
      const res = await adminTenantCapacityApi.deleteBlockedDay(apiUrl, o.id);
      setConfirmDeleteId(null);
      if (res.error) {
        setBusyId(null);
        setRowError(res.error);
        return;
      }
      await load('refresh');
      setBusyId(null);
      setSaved(`${formatBlockedDate(o.date)} is open again. Customers can book it now.`);
    },
    [apiUrl, load],
  );

  const horizonControl = (
    <Select
      name="blockedDaysHorizon"
      aria-label="How far ahead to show"
      value={horizon}
      onChange={(e) => setHorizon(e.target.value as '90' | '365')}
      options={HORIZON_OPTIONS}
      data-testid="blocked-days-horizon"
    />
  );

  // ── Loading ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <Card title="Blocked days">
        <div data-testid="blocked-days-loading">
          <Skeleton variant="text" lines={4} />
        </div>
      </Card>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (status === 'error' || !view) {
    return (
      <Card title="Blocked days">
        <div data-testid="blocked-days-error">
          <Alert variant="error">{loadError ?? 'Could not load blocked days.'}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const horizonLabel = HORIZON_OPTIONS.find((o) => o.value === horizon)?.label.toLowerCase() ?? 'the next 90 days';
  const capVisible = form.reason === 'special_event' || form.reason === 'expanded';

  return (
    <Card
      title="Blocked days"
      subtitle="Days you are closed or running at a different limit. The customer app stops offering a blocked day right away."
      headerRight={horizonControl}
    >
      <div className="ferro-settings__card" data-testid="blocked-days-ready">
        {rows.length === 0 ? (
          <p className="ferro-settings__teaching" data-testid="blocked-days-empty">
            No blocked days in the {horizonLabel}.
            {canEdit ? ' Block one below — assessments, daycare and boarding all follow it.' : ''}
          </p>
        ) : (
          <ul className="ferro-blocked__list" data-testid="blocked-days-list">
            {rows.map((o) => {
              const meta = reasonMeta(o.reason);
              const name = o.serviceTypeId === null ? null : (serviceNameById.get(o.serviceTypeId) ?? null);
              const busy = busyId === o.id;
              return (
                <li key={o.id} className="ferro-blocked__row" data-testid={`blocked-day-${o.id}`}>
                  <div className="ferro-blocked__date">{formatBlockedDate(o.date, true)}</div>
                  <div className="ferro-blocked__body">
                    <Badge variant={meta.badge}>{meta.chip}</Badge>
                    <span className="ferro-blocked__sentence">{overrideSentence(o, name)}</span>
                    {o.description ? <span className="ferro-muted">— {o.description}</span> : null}
                  </div>
                  {canEdit ? (
                    <div className="ferro-blocked__actions">
                      {confirmDeleteId === o.id ? (
                        <>
                          <Button size="sm" variant="danger" isLoading={busy} onClick={() => void handleRemove(o)} data-testid={`blocked-day-remove-confirm-${o.id}`}>
                            Confirm remove
                          </Button>
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmDeleteId(null)}>
                            Keep
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmDeleteId(o.id)} data-testid={`blocked-day-remove-${o.id}`}>
                          Remove
                        </Button>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {rowError ? (
          <div data-testid="blocked-days-row-error">
            <Alert variant="error">{rowError}</Alert>
          </div>
        ) : null}
        {saved ? (
          <div data-testid="blocked-days-saved">
            <Alert variant="success">{saved}</Alert>
          </div>
        ) : null}

        {canEdit ? (
          <form
            className="ferro-form ferro-settings__form"
            // noValidate: the bounds are explained in our own words under the
            // field; the browser's native bubble would pre-empt that copy.
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
            aria-label="Block a day"
            data-testid="blocked-days-form"
          >
            <h3 className="ferro-settings__subheading">Block a day</h3>
            <div className="ferro-settings__form-grid">
              <Input
                name="blockedDate"
                label="Day"
                type="date"
                min={today}
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                data-testid="blocked-day-date"
              />
              <Select
                name="blockedReason"
                label="What's happening"
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value as TenantCapacityOverrideReason }))}
                options={REASONS.map((r) => ({ value: r.value, label: r.label }))}
                data-testid="blocked-day-reason"
              />
              <Select
                name="blockedService"
                label="Applies to"
                value={form.serviceTypeId}
                onChange={(e) => setForm((f) => ({ ...f, serviceTypeId: e.target.value }))}
                options={[
                  { value: '', label: 'All services' },
                  ...activeServices.map((s) => ({ value: s.id, label: s.displayName })),
                ]}
                data-testid="blocked-day-service"
              />
              {capVisible ? (
                <Input
                  name="blockedCap"
                  label="Limit that day"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={5000}
                  step={1}
                  value={form.cap}
                  onChange={(e) => setForm((f) => ({ ...f, cap: e.target.value }))}
                  helperText={
                    form.reason === 'expanded'
                      ? 'How many dogs to take that day instead of the usual limit.'
                      : 'Leave blank to close that day; a number replaces the usual limit.'
                  }
                  data-testid="blocked-day-cap"
                />
              ) : null}
            </div>
            <Input
              name="blockedNote"
              label="Note (optional)"
              maxLength={200}
              placeholder="Staff training, deep clean, holiday hours…"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              data-testid="blocked-day-note"
            />
            {formError ? (
              <div data-testid="blocked-days-form-error">
                <Alert variant="error">{formError}</Alert>
              </div>
            ) : null}
            <div className="ferro-settings__actions">
              <Button type="submit" variant="primary" isLoading={isSaving} disabled={form.date === '' || isSaving} data-testid="blocked-day-submit">
                {form.reason === 'closure' || form.reason === 'holiday' ? 'Block this day' : 'Save this day'}
              </Button>
            </div>
          </form>
        ) : (
          <p className="ferro-muted" data-testid="blocked-days-readonly-note">
            Only an owner or manager can block or unblock a day.
          </p>
        )}
      </div>
    </Card>
  );
}
